import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Zap, Home, Building2, Palette, Users, Award, MapPin, Calendar } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { SeoHead } from '../components/website/SeoHead';
import { brandName } from '../hooks/useCompanyBrand';
import { useCMSContent, getSection, getSectionByType } from '../hooks/useCMSContent';
import axios from 'axios';
import { brandTitle } from '../seo/seoCore';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Icon mapping for dynamic features
const iconMap = { Award, Zap, Building2, MapPin, Home, Users, Palette };

// Fallback features if CMS is empty
const defaultFeatures = [
  { icon: Award, title: 'Custom-Made', description: 'Made to your measurements' },
  { icon: Zap, title: 'Motorized & Manual', description: 'Smart home integration available' },
  { icon: Building2, title: 'Residential & Commercial', description: 'Solutions for every project scale' },
  { icon: MapPin, title: 'Brooklyn Service', description: 'Local support and installation' },
];

const audiences = [
  { icon: Home, title: 'Homeowners', description: 'Custom solutions for your living spaces' },
  { icon: Users, title: 'Interior Designers', description: 'Trade program with dedicated support' },
  { icon: Building2, title: 'Architects', description: 'Specification assistance and samples' },
  { icon: Palette, title: 'Builders & Developers', description: 'Volume pricing for multi-unit projects' },
];

export const HomePage = () => {
  const [categories, setCategories] = useState([]);
  const [gallery, setGallery] = useState([]);
  const { sections, seo, loading: cmsLoading } = useCMSContent('home');

  // Get CMS sections
  const heroSection = getSectionByType(sections, 'hero');
  const featuresSection = getSectionByType(sections, 'features');

  // Use CMS features or fallback
  const features = featuresSection?.items?.length > 0 
    ? featuresSection.items.map(item => ({
        ...item,
        icon: iconMap[item.icon] || Award
      }))
    : defaultFeatures;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, galRes] = await Promise.all([
          axios.get(`${API_URL}/products/categories`),
          axios.get(`${API_URL}/gallery`)
        ]);
        setCategories(catRes.data);
        setGallery(galRes.data.slice(0, 6));
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, []);

  // Update document title from CMS SEO
  useEffect(() => {
    if (seo?.title) {
      document.title = brandTitle(seo.title);
    }
  }, [seo]);

  return (
    <Layout>
      <SeoHead seo={seo} path="/" fallbackTitle={`${brandName()} | Luxury Custom Window Treatments in Brooklyn`} />
      {/* Hero Section - CMS Driven */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src={heroSection?.image?.url || "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=85"}
            alt={heroSection?.image?.alt_text || "Modern interior with custom window treatments"}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/90 via-neutral-900/70 to-neutral-900/40" />
        </div>

        <div className="container-xl relative z-10 pt-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-white mb-6">
              {heroSection?.title ? (
                heroSection.title.includes(',') ? (
                  <>
                    {heroSection.title.split(',')[0]},
                    <br />
                    <span className="text-amber-400">{heroSection.title.split(',')[1]}</span>
                  </>
                ) : (
                  heroSection.title
                )
              ) : (
                <>
                  Custom Window Treatments,
                  <br />
                  <span className="text-amber-400">Based in Brooklyn</span>
                </>
              )}
            </h1>
            <p className="text-xl text-neutral-300 mb-10 max-w-2xl leading-relaxed">
              {heroSection?.content || "Apollo Shades creates premium custom roller shades, motorized drapes, and Roman shades for residential and commercial spaces, custom-made to your measurements."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to={heroSection?.button_link || "/quote"}>
                <Button className="btn-primary !bg-white !text-neutral-900 hover:!bg-neutral-100" data-testid="hero-quote-btn">
                  {heroSection?.button_text || "Request a Quote"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/consultation">
                <Button className="btn-primary !bg-amber-600 !text-white hover:!bg-amber-700" data-testid="hero-consultation-btn">
                  <Calendar className="w-4 h-4 mr-2" />
                  Free Consultation
                </Button>
              </Link>
              <Link to="/products">
                <Button className="btn-outline-light" data-testid="hero-products-btn">
                  Explore Products
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2"
          >
            <div className="w-1 h-2 bg-white/50 rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Why Apollo Shades - CMS Driven */}
      <section className="section-padding bg-neutral-50" data-testid="features-section">
        <div className="container-xl">
          <div className="text-center mb-16">
            <span className="label-overline text-amber-700">Why Apollo Shades</span>
            <h2 className="mt-4 text-neutral-900">{featuresSection?.title || 'Precision Craftsmanship'}</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center border border-neutral-200 bg-white">
                    <IconComponent className="w-6 h-6 text-amber-700" strokeWidth={1.5} />
                  </div>
                  <h4 className="text-lg font-semibold text-neutral-900 mb-2">{feature.title}</h4>
                  <p className="text-neutral-600 text-sm">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="section-padding" data-testid="products-section">
        <div className="container-xl">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
            <div>
              <span className="label-overline text-amber-700">Our Products</span>
              <h2 className="mt-4 text-neutral-900">Window Treatment Solutions</h2>
            </div>
            <Link to="/products" className="text-sm font-medium text-neutral-900 link-underline">
              View All Products
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={`/products/${category.id}`} className="card-product block" data-testid={`product-card-${category.id}`}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img 
                      src={category.image} 
                      alt={category.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-neutral-900 mb-2">{category.name}</h3>
                    <p className="text-neutral-600 text-sm mb-4 line-clamp-2">{category.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-500">From ${category.starting_price}</span>
                      <span className="text-sm font-medium text-neutral-900 flex items-center gap-1 group-hover:gap-2 transition-all">
                        Learn More <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Manufacturing Section */}
      <section className="section-padding bg-neutral-900 text-white" data-testid="manufacturing-section">
        <div className="container-xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="label-overline text-amber-400">Made to Measure</span>
              <h2 className="mt-4 text-white mb-6">Built to Order, Built to Last</h2>
              <div className="space-y-6 text-neutral-300">
                <p>
                  Every Apollo Shades product is custom-made to your measurements. We guide every step of the process, from consultation and precision measuring to professional installation.
                </p>
                <p>
                  Based in Brooklyn, we offer consultation and installation throughout New York City, with truly custom solutions for each project.
                </p>
              </div>
              <ul className="mt-8 space-y-4">
                {['Precision fabrication', 'Quality materials and components', 'Tailored solutions for each project', 'Consultation and installation across NYC'].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 bg-amber-400" />
                    <span className="text-neutral-300">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/about" className="inline-block mt-10">
                <Button className="bg-white text-neutral-900 hover:bg-neutral-100">
                  Learn About Us
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="/img/catalog/built-to-order-workroom.jpg"
                alt="Custom shade craftsmanship"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-amber-600 text-white p-6 hidden lg:block">
                <p className="text-4xl font-serif font-semibold">15+</p>
                <p className="text-sm uppercase tracking-wider mt-1">Years Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="section-padding" data-testid="audiences-section">
        <div className="container-xl">
          <div className="text-center mb-16">
            <span className="label-overline text-amber-700">Who We Serve</span>
            <h2 className="mt-4 text-neutral-900">Solutions for Every Client</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {audiences.map((audience, index) => (
              <motion.div
                key={audience.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 border border-neutral-200 hover:border-neutral-400 transition-colors"
              >
                <audience.icon className="w-8 h-8 text-amber-700 mb-4" strokeWidth={1.5} />
                <h4 className="text-lg font-semibold text-neutral-900 mb-2">{audience.title}</h4>
                <p className="text-neutral-600 text-sm">{audience.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="section-padding bg-neutral-50" data-testid="gallery-section">
        <div className="container-xl">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
            <div>
              <span className="label-overline text-amber-700">Recent Projects</span>
              <h2 className="mt-4 text-neutral-900">Inspiration Gallery</h2>
            </div>
            <Link to="/gallery" className="text-sm font-medium text-neutral-900 link-underline">
              View All Projects
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {gallery.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative aspect-square overflow-hidden group cursor-pointer"
              >
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-neutral-900/0 group-hover:bg-neutral-900/60 transition-colors flex items-end p-4">
                  <div className="translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                    <p className="text-white font-medium">{project.title}</p>
                    <p className="text-neutral-300 text-sm">{project.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-neutral-900" data-testid="cta-section">
        <div className="container-xl text-center">
          <h2 className="text-white mb-6">Ready to Start Your Project?</h2>
          <p className="text-neutral-400 max-w-2xl mx-auto mb-10">
            Whether you're a homeowner, designer, or contractor, we're here to help create the perfect window treatment solution for your space.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote">
              <Button className="btn-primary !bg-white !text-neutral-900 hover:!bg-neutral-100">
                Get a Quote
              </Button>
            </Link>
            <Link to="/consultation">
              <Button className="btn-primary !bg-amber-600 !text-white hover:!bg-amber-700">
                <Calendar className="w-4 h-4 mr-2" />
                Free Consultation
              </Button>
            </Link>
            <Link to="/contact">
              <Button className="btn-outline-light">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};
